import {
  Container,
  Stack,
  Typography,
  Box,
  LinearProgress,
  Card,
  Avatar,
  Chip,
  useTheme,
  alpha,
  Fade,
  Paper
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useEffect, useState } from "react";
import "./niche-pig-loader.css";
import pig from "./pig.png";

const NichePigLoading = (props: any) => {
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  
  const messages = [
    {
      title: "Extracting Market Data",
      description: "Analyzing search trends and collecting real-time market intelligence",
      icon: <SearchIcon />,
      progressTarget: 25
    },
    {
      title: "Analyzing Buyer Sentiment",
      description: "Processing reviews and feedback to understand customer needs",
      icon: <TrendingUpIcon />,
      progressTarget: 50
    },
    {
      title: "Identifying Profitable Niches",
      description: "Discovering high-potential opportunities in your market",
      icon: <LightbulbIcon />,
      progressTarget: 75
    },
    {
      title: "Generating Your Report",
      description: "Compiling comprehensive insights and recommendations",
      icon: <AutoAwesomeIcon />,
      progressTarget: 95
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % messages.length;
        setProgress(messages[nextIndex].progressTarget);
        return nextIndex;
      });
    }, 10000);
    
    // Set initial progress
    setProgress(messages[0].progressTarget);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Container
        maxWidth="lg"
        sx={{
          px: 5,
          py: 8,
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Card
          sx={{
            width: '100%',
            p: 6,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          }}
        >
          <Stack spacing={4} alignItems="center">
            {/* Animated Icon */}
            <Box sx={{ position: 'relative' }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `3px solid ${theme.palette.primary.main}`,
                }}
              >
                <Stack className="pig-loader">
                  <img src={pig} width="50" alt="Loading" />
                </Stack>
              </Avatar>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -5,
                  right: -5,
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                    '50%': {
                      transform: 'scale(1.1)',
                      opacity: 0.8,
                    },
                    '100%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: theme.palette.primary.main,
                    border: `2px solid ${theme.palette.background.paper}`,
                  }}
                >
                  {messages[index].icon}
                </Avatar>
              </Box>
            </Box>

            {/* Status Messages */}
            <Fade in={true} timeout={500} key={index}>
              <Stack spacing={2} alignItems="center">
                <Typography 
                  variant="h5" 
                  align="center"
                  sx={{ 
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {messages[index].title}
                </Typography>
                <Typography 
                  align="center" 
                  color="text.secondary" 
                  variant="body1"
                  sx={{ maxWidth: 600 }}
                >
                  {messages[index].description}
                </Typography>
              </Stack>
            </Fade>

            {/* Progress Bar */}
            <Box sx={{ width: '100%', maxWidth: 600 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      }
                    }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 35 }}>
                  {`${Math.round(progress)}%`}
                </Typography>
              </Box>
            </Box>

            {/* Progress Steps */}
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              {messages.map((msg, idx) => (
                <Chip
                  key={idx}
                  icon={idx < index ? <CheckCircleIcon /> : idx === index ? msg.icon : undefined}
                  label={`Step ${idx + 1}`}
                  size="small"
                  color={idx <= index ? "primary" : "default"}
                  variant={idx === index ? "filled" : "outlined"}
                  sx={{
                    transition: 'all 0.3s',
                    ...(idx <= index && {
                      bgcolor: idx === index ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.1),
                      borderColor: theme.palette.primary.main,
                      color: idx === index ? 'white' : theme.palette.primary.main,
                    })
                  }}
                />
              ))}
            </Stack>

            {/* Info Box */}
            <Paper
              sx={{
                p: 2,
                width: '100%',
                maxWidth: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                Pro Tip
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                Our AI discovers hidden niches with 85% accuracy in profitability prediction, helping you find untapped markets before your competitors.
              </Typography>
            </Paper>
          </Stack>
        </Card>
      </Container>
    </>
  );
};

export default NichePigLoading;